import { FEATURE_CONTEXTS } from './contexts';
import { getFeature, listFeaturesByContext, getFeaturePorSituacao } from './registry';
import { isTourDismissed, isHintSetDismissed } from './storage';
import {
  startTour,
  reabrirTour,
  podeReabrirTour,
  isTourActive,
  destroyActiveTour,
} from './tourRunner';
import { showHintSet, hideActiveHints, isHintActive } from './hints';

function featureFoiVisto(userId, feature) {
  if (feature.kind === 'hint') {
    return isHintSetDismissed(userId, feature.id, feature.version);
  }
  return isTourDismissed(userId, feature.id, feature.version);
}

function requirementsSatisfied(userId, feature) {
  return (feature.requires || []).every(requiredId => {
    const required = getFeature(requiredId);
    if (!required) return false;
    return featureFoiVisto(userId, required);
  });
}

function featureTemConteudoVisivel(feature, ctx = {}) {
  if (feature.kind === 'hint') {
    const items = typeof feature.hints === 'function' ? feature.hints(ctx) : feature.hints || [];
    return items.some(item => {
      if (!item.element) return true;
      if (typeof item.element === 'string') return Boolean(document.querySelector(item.element));
      return true;
    });
  }

  const steps = typeof feature.steps === 'function' ? feature.steps(ctx) : feature.steps || [];
  return steps.some(step => {
    if (!step.element) return true;
    return Boolean(document.querySelector(step.element));
  });
}

function featurePertenceASituacao(feature, ctx = {}) {
  if (feature.situation == null) return true;
  if (ctx.situation == null || ctx.situation === '') return false;
  return Number(feature.situation) === Number(ctx.situation);
}

function featuresDoContexto(contextId, ctx = {}, options = {}) {
  const context = FEATURE_CONTEXTS[contextId];
  if (!context) return [];

  let features = listFeaturesByContext(contextId);

  // Contexto por situação: tour + hints da fase atual (ordenados por priority).
  // Sem situation no ctx → lista vazia (não faz fallback para todas as features).
  if (context.matchBy === 'situation') {
    if (ctx.situation == null || ctx.situation === '') {
      features = [];
    } else {
      const situacao = Number(ctx.situation);
      features = features.filter(
        feature => feature.situation != null && Number(feature.situation) === situacao,
      );
    }
  }

  if (options.scope) {
    features = features.filter(feature => feature.scopes.includes(options.scope));
  }

  if (Array.isArray(options.featureIds) && options.featureIds.length) {
    const allowed = new Set(options.featureIds);
    features = features.filter(feature => allowed.has(feature.id));
  }

  return features.filter(feature => featurePertenceASituacao(feature, ctx));
}

/**
 * Candidates for auto-run in a context, ordered by priority.
 * Não exige DOM ainda — prepare pode revelar elementos.
 */
export function listCandidates(contextId, userId, ctx = {}, options = {}) {
  if (userId == null) return [];

  return featuresDoContexto(contextId, ctx, options).filter(feature => {
    if (featureFoiVisto(userId, feature)) return false;
    if (!requirementsSatisfied(userId, feature)) return false;
    if (typeof feature.canQueue === 'function') {
      return feature.canQueue(ctx);
    }
    return true;
  });
}

/**
 * Opções para FeatureTourHelp (tours com help:true que podem reabrir agora).
 */
export function listHelpOptions(contextId, userId, ctx = {}, options = {}) {
  if (userId == null) return [];

  return featuresDoContexto(contextId, ctx, options)
    .filter(feature => feature.help && feature.kind === 'tour')
    .filter(feature => podeReabrirTour(feature.id, userId, ctx))
    .map(feature => ({
      id: feature.id,
      label: feature.label,
      disponivel: true,
    }));
}

async function prepareFeature(feature, prepares = {}) {
  if (!feature) return;
  const prepare = prepares[feature.id];
  if (typeof prepare === 'function') {
    await prepare(feature);
  }
}

function launchFeature(feature, userId, ctx, { force = false, onComplete } = {}) {
  if (!feature) return false;
  if (feature.kind === 'hint') {
    return showHintSet(feature.id, userId, ctx);
  }
  if (force) {
    return reabrirTour(feature.id, userId, ctx);
  }
  return startTour(feature.id, userId, ctx, { onComplete });
}

/**
 * Roda a primeira feature elegível do contexto (tour ou hint).
 * Executa prepare antes de validar DOM (ex.: abrir detalhe da tabela).
 * Ao terminar um tour automático, reentra no contexto para a próxima (ex.: hints).
 * @returns {Promise<string|null>} id da feature iniciada
 */
export async function runContext(contextId, userId, ctx = {}, options = {}) {
  if (userId == null) return null;
  if (isTourActive() || isHintActive()) return null;

  const candidates = listCandidates(contextId, userId, ctx, options);
  const prepares = options.prepares || {};

  // eslint-disable-next-line no-restricted-syntax
  for (const feature of candidates) {
    // eslint-disable-next-line no-await-in-loop
    await prepareFeature(feature, prepares);
    if (isTourActive() || isHintActive()) return null;

    const podeIniciar =
      (typeof feature.canStart !== 'function' || feature.canStart(ctx)) &&
      featureTemConteudoVisivel(feature, ctx);

    const onComplete =
      feature.kind === 'tour'
        ? () => {
            runContext(contextId, userId, ctx, options);
          }
        : undefined;

    if (podeIniciar && launchFeature(feature, userId, ctx, { onComplete })) {
      return feature.id;
    }
  }

  return null;
}

/**
 * Reabre uma feature sob demanda (ajuda / repetir).
 */
export async function replayFeature(featureId, userId, ctx = {}, options = {}) {
  const feature = getFeature(featureId);
  if (!feature || userId == null) return false;
  if (!featurePertenceASituacao(feature, ctx)) return false;

  await prepareFeature(feature, options.prepares || {});
  return launchFeature(feature, userId, ctx, { force: true });
}

export function stopFeatures() {
  destroyActiveTour();
  hideActiveHints();
}

export { getFeaturePorSituacao };
